import { useEffect, useState, useCallback } from "react";
import { Plus, Landmark, Trash2, Save, Bell, BellOff, Check } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const MONTH_NAMES_ES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const MONTH_NAMES_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Página de Tesorería. Un doc por (tenant, año, mes) con:
 *   - saldo en bancos
 *   - préstamo pendiente
 *   - saldo neto = bancos − préstamo (calculado)
 * Muestra tabla editable + gráfica de evolución del saldo neto.
 */
export default function Treasury() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const monthNames = lang === "es" ? MONTH_NAMES_ES : MONTH_NAMES_EN;
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState({ entries: [], history: [] });
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState({ month: now.getMonth() + 1, bank_balance: "", loan_balance: "", notes: "" });
  const [saving, setSaving] = useState(false);
  // Edición inline por fila: {[id]: {bank_balance, loan_balance, notes}}
  const [edits, setEdits] = useState({});
  const [banksEditing, setBanksEditing] = useState(null); // { entry }
  const currency = user?.base_currency || "EUR";
  const fmt = (v) => new Intl.NumberFormat(lang === "es" ? "es-ES" : "en-US", {
    style: "currency", currency, maximumFractionDigits: 0, useGrouping: "always",
  }).format(v || 0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/treasury", { params: { year } });
      setData(data);
      setEdits({});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const submitDraft = async () => {
    if (!draft.month) return;
    setSaving(true);
    try {
      await api.post("/treasury", {
        year,
        month: Number(draft.month),
        bank_balance: parseFloat(draft.bank_balance) || 0,
        loan_balance: parseFloat(draft.loan_balance) || 0,
        notes: draft.notes,
      });
      toast.success(lang === "es" ? "Guardado" : "Saved");
      setDraft({ month: now.getMonth() + 1, bank_balance: "", loan_balance: "", notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (entry) => {
    const patch = edits[entry.id];
    if (!patch) return;
    try {
      await api.put(`/treasury/${entry.id}`, {
        year: entry.year,
        month: entry.month,
        bank_balance: patch.bank_balance != null ? parseFloat(patch.bank_balance) : entry.bank_balance,
        loan_balance: patch.loan_balance != null ? parseFloat(patch.loan_balance) : entry.loan_balance,
        notes: patch.notes != null ? patch.notes : entry.notes,
      });
      toast.success(lang === "es" ? "Actualizado" : "Updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(lang === "es" ? "¿Eliminar esta entrada?" : "Delete this entry?")) return;
    try {
      await api.delete(`/treasury/${id}`);
      toast.success(lang === "es" ? "Eliminado" : "Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Meses ya usados → no dejar añadir duplicados desde el formulario.
  const usedMonths = new Set(data.entries.map((e) => e.month));

  return (
    <div className="space-y-6" data-testid="treasury-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Landmark size={24} className="text-[var(--primary)]" />
            {lang === "es" ? "Tesorería" : "Treasury"}
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            {lang === "es"
              ? "Registra por mes el saldo en bancos y el préstamo pendiente. Ve la evolución del saldo neto en tiempo real."
              : "Log monthly bank balance and pending loan. Track net balance evolution over time."}
          </p>
        </div>
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          data-testid="treasury-year"
          className="rounded border border-[var(--border-color)] bg-[var(--surface)] px-3 py-2 font-mono-num"
        >
          {Array.from({ length: 6 }, (_, i) => now.getFullYear() - 4 + i).map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {/* Recordatorio mensual: aparece cuando toca (día ≥ día configurado y
          no hay entrada para el mes en curso). */}
      <ReminderBanner
        reminder={data.reminder}
        lang={lang}
        onDismiss={async () => {
          try {
            await api.post("/treasury/reminder/dismiss");
            toast.success(lang === "es" ? "Recordatorio silenciado este mes" : "Reminder silenced for this month");
            load();
          } catch (err) { toast.error(formatApiError(err)); }
        }}
        onChangeDay={async (day) => {
          try {
            await api.put("/treasury/reminder", { day });
            toast.success(lang === "es" ? `Aviso el día ${day} de cada mes` : `Reminder on day ${day} of each month`);
            load();
          } catch (err) { toast.error(formatApiError(err)); }
        }}
      />

      {/* KPI resumen (último mes registrado) */}
      {data.history.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <KpiCard
            label={lang === "es" ? "Último saldo bancos" : "Latest bank balance"}
            value={fmt(data.history[data.history.length - 1].bank)}
            testid="kpi-bank"
          />
          <KpiCard
            label={lang === "es" ? "Préstamo pendiente" : "Pending loan"}
            value={fmt(data.history[data.history.length - 1].loan)}
            accent="negative"
            testid="kpi-loan"
          />
          <KpiCard
            label={lang === "es" ? "Saldo neto" : "Net balance"}
            value={fmt(data.history[data.history.length - 1].net)}
            accent={data.history[data.history.length - 1].net >= 0 ? "positive" : "negative"}
            testid="kpi-net"
          />
        </div>
      )}

      {/* Gráfica de evolución */}
      {data.history.length >= 2 && (
        <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-6" data-testid="treasury-chart">
          <h2 className="font-display text-lg font-semibold mb-3">
            {lang === "es" ? "Evolución del saldo" : "Balance evolution"}
          </h2>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.history.map((h) => ({ ...h, label: monthNames[h.month - 1] }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eaeae6" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="bank" name={lang === "es" ? "Bancos" : "Bank"} stroke="#2C4C3B" strokeWidth={2} dot />
                <Line type="monotone" dataKey="loan" name={lang === "es" ? "Préstamo" : "Loan"} stroke="#A44C40" strokeWidth={2} dot />
                <Line type="monotone" dataKey="net" name={lang === "es" ? "Neto" : "Net"} stroke="#3F5C7A" strokeWidth={2} strokeDasharray="4 3" dot />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Formulario para añadir nuevo mes */}
      <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-6" data-testid="treasury-new-form">
        <h2 className="font-display text-lg font-semibold mb-3">
          {lang === "es" ? "Añadir mes" : "Add month"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Mes" : "Month"}</Label>
            <Select value={String(draft.month)} onValueChange={(v) => setDraft({ ...draft, month: Number(v) })}>
              <SelectTrigger data-testid="treasury-draft-month"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <SelectItem key={m} value={String(m)} disabled={usedMonths.has(m)}>
                    {monthNames[m - 1]}{usedMonths.has(m) ? " · ✓" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Bancos" : "Bank"}</Label>
            <Input
              type="number"
              step="0.01"
              value={draft.bank_balance}
              onChange={(e) => setDraft({ ...draft, bank_balance: e.target.value })}
              placeholder="0.00"
              className="font-mono-num"
              data-testid="treasury-draft-bank"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Préstamo" : "Loan"}</Label>
            <Input
              type="number"
              step="0.01"
              value={draft.loan_balance}
              onChange={(e) => setDraft({ ...draft, loan_balance: e.target.value })}
              placeholder="0.00"
              className="font-mono-num"
              data-testid="treasury-draft-loan"
            />
          </div>
          <div className="space-y-1 md:col-span-1">
            <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Notas" : "Notes"}</Label>
            <Input
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              placeholder={lang === "es" ? "Opcional" : "Optional"}
              data-testid="treasury-draft-notes"
            />
          </div>
          <Button
            onClick={submitDraft}
            disabled={saving || usedMonths.has(draft.month)}
            data-testid="treasury-draft-submit"
            className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
          >
            <Plus size={14} className="mr-1" />
            {saving ? "..." : (lang === "es" ? "Guardar" : "Save")}
          </Button>
        </div>
      </div>

      {/* Tabla de entradas */}
      <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--surface-hover)] border-b border-[var(--border-color)]">
            <tr>
              <th className="text-left py-2.5 px-3 font-medium text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                {lang === "es" ? "Mes" : "Month"}
              </th>
              <th className="text-right py-2.5 px-3 font-medium text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                {lang === "es" ? "Bancos" : "Bank"}
              </th>
              <th className="text-right py-2.5 px-3 font-medium text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                {lang === "es" ? "Préstamo" : "Loan"}
              </th>
              <th className="text-right py-2.5 px-3 font-medium text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                {lang === "es" ? "Neto" : "Net"}
              </th>
              <th className="text-left py-2.5 px-3 font-medium text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                {lang === "es" ? "Notas" : "Notes"}
              </th>
              <th className="w-24" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="6" className="text-center py-8 text-[var(--text-muted)]">…</td></tr>
            )}
            {!loading && data.entries.length === 0 && (
              <tr><td colSpan="6" className="text-center py-12 text-[var(--text-muted)]">
                {lang === "es" ? "Aún no hay entradas para este año" : "No entries for this year yet"}
              </td></tr>
            )}
            {data.entries.map((e) => {
              const edit = edits[e.id] || {};
              const isDirty = Object.keys(edit).length > 0;
              return (
                <tr key={e.id} className="border-b border-[var(--border-color)]" data-testid={`treasury-row-${e.month}`}>
                  <td className="py-2 px-3 font-medium">{monthNames[e.month - 1]}</td>
                  <td className="py-2 px-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Input
                        type="number"
                        step="0.01"
                        defaultValue={e.bank_balance}
                        onChange={(ev) => setEdits({ ...edits, [e.id]: { ...edit, bank_balance: ev.target.value } })}
                        className="h-8 text-right font-mono-num"
                        data-testid={`treasury-bank-${e.month}`}
                        disabled={(e.banks?.length || 0) > 0}
                        title={(e.banks?.length || 0) > 0 ? (lang === "es" ? "Suma de bancos — edítalos con el botón de la derecha" : "Aggregated from banks — edit them from the button") : ""}
                      />
                      <button
                        type="button"
                        onClick={() => setBanksEditing({ entry: e })}
                        className="p-1 rounded hover:bg-[var(--surface-hover)] text-[var(--text-secondary)] hover:text-[var(--primary)]"
                        title={lang === "es" ? "Bancos" : "Banks"}
                        data-testid={`treasury-banks-${e.month}`}
                      >
                        <Landmark size={14} />
                      </button>
                    </div>
                    {(e.banks?.length || 0) > 0 && (
                      <div className="text-[10px] text-[var(--text-muted)] mt-0.5" data-testid={`treasury-banks-count-${e.month}`}>
                        {e.banks.length} {lang === "es" ? "bancos" : "banks"}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <Input
                      type="number"
                      step="0.01"
                      defaultValue={e.loan_balance}
                      onChange={(ev) => setEdits({ ...edits, [e.id]: { ...edit, loan_balance: ev.target.value } })}
                      className="h-8 text-right font-mono-num"
                      data-testid={`treasury-loan-${e.month}`}
                    />
                  </td>
                  <td className={`py-2 px-3 text-right font-mono-num font-semibold ${e.net_balance >= 0 ? "text-[var(--status-positive)]" : "text-[var(--status-negative)]"}`} data-testid={`treasury-net-${e.month}`}>
                    {fmt(e.net_balance)}
                  </td>
                  <td className="py-2 px-3">
                    <Input
                      defaultValue={e.notes}
                      onChange={(ev) => setEdits({ ...edits, [e.id]: { ...edit, notes: ev.target.value } })}
                      className="h-8 text-xs"
                      placeholder="—"
                    />
                  </td>
                  <td className="py-2 px-3 text-right space-x-1">
                    {isDirty && (
                      <Button size="sm" variant="outline" onClick={() => saveEdit(e)} data-testid={`treasury-save-${e.month}`}>
                        <Save size={12} />
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => remove(e.id)} data-testid={`treasury-delete-${e.month}`} className="text-[var(--status-negative)] hover:bg-[var(--status-negative-bg)]">
                      <Trash2 size={12} />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {banksEditing && (
        <BanksDialog
          entry={banksEditing.entry}
          lang={lang}
          currency={currency}
          onClose={() => setBanksEditing(null)}
          onSaved={() => { setBanksEditing(null); load(); }}
        />
      )}
    </div>
  );
}


function BanksDialog({ entry, lang, currency, onClose, onSaved }) {
  // iter 82.7r — asign stable per-row UIDs so React key is stable on reorder.
  const seed = (entry.banks && entry.banks.length > 0)
    ? entry.banks
    : (entry.bank_balance ? [{ name: "Principal", balance: entry.bank_balance }] : [{ name: "", balance: 0 }]);
  const initial = seed.map((b) => ({ _uid: (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `b-${Math.random().toString(36).slice(2)}`), ...b }));
  const [banks, setBanks] = useState(initial);
  const [saving, setSaving] = useState(false);
  const fmt = (v) => new Intl.NumberFormat(lang === "es" ? "es-ES" : "en-US", {
    style: "currency", currency, maximumFractionDigits: 2, useGrouping: "always",
  }).format(v || 0);
  const total = banks.reduce((s, b) => s + (parseFloat(b.balance) || 0), 0);

  const setBank = (idx, key, val) => setBanks((prev) => prev.map((b, i) => i === idx ? { ...b, [key]: val } : b));
  const addBank = () => setBanks((prev) => [...prev, { _uid: (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `b-${Math.random().toString(36).slice(2)}`), name: "", balance: 0 }]);
  const removeBank = (idx) => setBanks((prev) => prev.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = banks
        .filter((b) => (b.name || "").trim() || (parseFloat(b.balance) || 0) !== 0)
        .map((b) => ({ name: (b.name || "").trim() || "Banco", balance: parseFloat(b.balance) || 0 }));
      await api.put(`/treasury/${entry.id}`, {
        year: entry.year,
        month: entry.month,
        bank_balance: cleaned.reduce((s, b) => s + b.balance, 0),
        loan_balance: entry.loan_balance || 0,
        notes: entry.notes || "",
        banks: cleaned,
      });
      toast.success(lang === "es" ? "Bancos guardados" : "Banks saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
      data-testid="treasury-banks-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg shadow-lg max-w-lg w-full p-4 space-y-3"
      >
        <div>
          <h2 className="font-display text-lg font-bold tracking-tight">
            {lang === "es" ? "Bancos" : "Banks"} · {entry.year}/{entry.month}
          </h2>
          <p className="text-xs text-[var(--text-muted)]">
            {lang === "es"
              ? "Añade cada cuenta bancaria con su saldo. El total se suma automáticamente."
              : "List each bank account with its balance. Total is auto-summed."}
          </p>
        </div>
        <div className="max-h-[50vh] overflow-y-auto space-y-1.5">
          {banks.map((b, idx) => (
            <div key={b._uid || `b-${idx}`} className="grid grid-cols-12 gap-2 items-center" data-testid={`treasury-bank-row-${idx}`}>
              <Input
                className="col-span-7 h-8 text-sm"
                value={b.name || ""}
                onChange={(ev) => setBank(idx, "name", ev.target.value)}
                placeholder={lang === "es" ? "Nombre del banco" : "Bank name"}
                data-testid={`treasury-bank-name-${idx}`}
              />
              <Input
                type="number"
                step="0.01"
                className="col-span-4 h-8 text-sm text-right font-mono-num"
                value={b.balance ?? 0}
                onChange={(ev) => setBank(idx, "balance", ev.target.value)}
                data-testid={`treasury-bank-balance-${idx}`}
              />
              <button
                onClick={() => removeBank(idx)}
                className="col-span-1 justify-self-end text-[var(--status-negative)]"
                title={lang === "es" ? "Quitar" : "Remove"}
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={addBank} data-testid="treasury-bank-add-btn">
            <Plus size={12} className="mr-1" />
            {lang === "es" ? "Añadir banco" : "Add bank"}
          </Button>
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-[var(--border-color)]">
          <p className="text-sm">
            <span className="text-[var(--text-muted)]">{lang === "es" ? "Total" : "Total"}:</span>{" "}
            <span className="font-mono-num font-bold" data-testid="treasury-banks-total">{fmt(total)}</span>
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={saving} data-testid="treasury-banks-cancel">
              {lang === "es" ? "Cancelar" : "Cancel"}
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={saving}
              className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
              data-testid="treasury-banks-save"
            >
              <Save size={12} className="mr-1" />
              {saving ? (lang === "es" ? "Guardando…" : "Saving…") : (lang === "es" ? "Guardar" : "Save")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, accent, testid }) {
  const accentClass = accent === "positive"
    ? "text-[var(--status-positive)]"
    : accent === "negative"
      ? "text-[var(--status-negative)]"
      : "text-[var(--text-primary)]";
  return (
    <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-[var(--text-muted)]">{label}</p>
      <p className={`font-display text-2xl font-semibold tracking-tight mt-1 font-mono-num ${accentClass}`}>{value}</p>
    </div>
  );
}


/**
 * Banner de recordatorio mensual. Renderiza:
 *   - Un aviso grande AMARILLO cuando `should_show=true` (día ≥ día configurado
 *     y no hay entrada para el mes en curso).
 *   - Siempre visible: un pill compacto con la configuración actual + input
 *     inline para modificar el día del recordatorio (1..28).
 */
function ReminderBanner({ reminder, lang, onDismiss, onChangeDay }) {
  const [editing, setEditing] = useState(false);
  const [dayDraft, setDayDraft] = useState(reminder?.day ?? 15);
  useEffect(() => { setDayDraft(reminder?.day ?? 15); }, [reminder?.day]);

  const saveDay = () => {
    const d = parseInt(dayDraft, 10);
    if (!Number.isInteger(d) || d < 1 || d > 28) {
      return;
    }
    onChangeDay(d);
    setEditing(false);
  };

  if (!reminder) return null;

  return (
    <div className="space-y-2" data-testid="treasury-reminder">
      {reminder.should_show && (
        <div
          data-testid="treasury-reminder-banner"
          className="rounded-lg border-2 border-[var(--status-warning)] bg-[var(--status-warning-bg)] p-4 flex items-start gap-3 flex-wrap"
        >
          <Bell size={20} className="text-[var(--status-warning)] shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-display font-semibold text-[var(--status-warning)]">
              {lang === "es"
                ? "Recordatorio: registra el saldo de este mes"
                : "Reminder: log this month's balance"}
            </p>
            <p className="text-xs text-[var(--text-secondary)] mt-1">
              {lang === "es"
                ? "Aún no has introducido bancos + préstamo para este mes. Mantén el histórico completo para ver la evolución real."
                : "You haven't logged bank + loan for the current month yet. Keep the timeline complete to see real evolution."}
            </p>
          </div>
          <button
            type="button"
            onClick={onDismiss}
            data-testid="treasury-reminder-dismiss"
            className="px-3 py-1.5 rounded text-xs font-medium border border-[var(--border-color)] bg-[var(--surface)] hover:bg-[var(--surface-hover)]"
          >
            <Check size={12} className="inline mr-1" />
            {lang === "es" ? "Marcar como recordado" : "Mark as done"}
          </button>
        </div>
      )}
      <div className="flex items-center gap-2 text-xs text-[var(--text-muted)] flex-wrap" data-testid="treasury-reminder-config">
        {editing ? (
          <>
            <span>{lang === "es" ? "Avisarme el día" : "Remind me on day"}</span>
            <input
              type="number"
              min="1"
              max="28"
              value={dayDraft}
              onChange={(e) => setDayDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") saveDay(); }}
              data-testid="treasury-reminder-day-input"
              className="w-16 h-7 px-2 rounded border border-[var(--border-color)] font-mono-num"
              autoFocus
            />
            <span>{lang === "es" ? "de cada mes" : "of each month"}</span>
            <button
              type="button"
              onClick={saveDay}
              data-testid="treasury-reminder-day-save"
              className="px-2 py-0.5 rounded bg-[var(--primary)] text-white hover:bg-[var(--primary-hover)]"
            >
              {lang === "es" ? "Guardar" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="text-[var(--text-muted)] hover:underline"
            >
              {lang === "es" ? "Cancelar" : "Cancel"}
            </button>
          </>
        ) : (
          <>
            <BellOff size={12} className="opacity-60" />
            <span>
              {lang === "es"
                ? `Aviso configurado para el día ${reminder.day} de cada mes (por email al tenant).`
                : `Reminder set for day ${reminder.day} of each month (email to tenant).`}
            </span>
            <button
              type="button"
              onClick={() => setEditing(true)}
              data-testid="treasury-reminder-edit"
              className="text-[var(--primary)] hover:underline font-medium"
            >
              {lang === "es" ? "Cambiar" : "Change"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

